import { Trip, TripAnnotation } from "@fairfleet/geotab";
import { useQueries, UseQueryResult } from "@tanstack/react-query";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_TRIP_ANNOTATION } from "@/constants/resultsLimits";
import { fetchTripsComplete } from "@/utilities/query/fetchTripsComplete";

export const AUDIT_TRIPS_QUERY_KEY = "auditTrips";

export interface DeviceTrips {
  deviceId: string;
  trips: Trip[];
  annotations: TripAnnotation[];
}

interface Args {
  deviceIds: string[];
  fromDate: Date;
  toDate: Date;
  /** Extra time before `fromDate` covered by the annotation query (stray annotations). */
  annotationLeadMs?: number;
  /** Extra time before `fromDate` covered by the trip query, e.g. for the previous trip's odometer. */
  tripLeadMs?: number;
  enabled?: boolean;
}

interface CombinedTrips {
  byDevice: Record<string, DeviceTrips>;
  loadedCount: number;
  totalCount: number;
  isFetching: boolean;
  isError: boolean;
  dataUpdatedAt: number;
}

// Module-level so TanStack sees a stable reference and does not re-run it on every render.
function combineTrips(results: UseQueryResult<DeviceTrips>[]): CombinedTrips {
  const byDevice: Record<string, DeviceTrips> = {};
  let loadedCount = 0;
  let dataUpdatedAt = 0;
  for (const result of results) {
    if (result.data) {
      byDevice[result.data.deviceId] = result.data;
      loadedCount++;
      dataUpdatedAt = Math.max(dataUpdatedAt, result.dataUpdatedAt);
    }
  }
  return {
    byDevice,
    loadedCount,
    totalCount: results.length,
    isFetching: results.some(r => r.isFetching),
    isError: results.some(r => r.isError),
    dataUpdatedAt,
  };
}

/**
 * Loads trips and trip annotations per device with one query each, so rows can appear as soon
 * as a vehicle has finished loading. `loadedCount / totalCount` drives the progress bar.
 */
export function useTripsForDevices({
  deviceIds,
  fromDate,
  toDate,
  annotationLeadMs = 0,
  tripLeadMs = 0,
  enabled = true,
}: Args) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQueries({
    queries: deviceIds.map(deviceId => ({
      queryKey: [AUDIT_TRIPS_QUERY_KEY, activeSessionId, deviceId, fromDate, toDate, annotationLeadMs, tripLeadMs],
      enabled,
      queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<DeviceTrips> => {
        const deviceSearch = { id: deviceId };
        const annotationFrom = new Date(fromDate.getTime() - annotationLeadMs);
        const tripFrom = new Date(fromDate.getTime() - tripLeadMs);
        const [trips, annotations] = await Promise.all([
          fetchTripsComplete(
            (search, resultsLimit) => geotabClient.get("Trip", search, resultsLimit, signal) as Promise<Trip[]>,
            { fromDate: tripFrom, toDate, searches: [{ deviceSearch }], signal },
          ),
          geotabClient.get(
            "TripAnnotation",
            { deviceSearch, fromDate: annotationFrom, toDate },
            RESULTS_LIMIT_TRIP_ANNOTATION,
            signal,
          ),
        ]);
        return { deviceId, trips, annotations };
      },
    })),
    combine: combineTrips,
  });
}
